import * as widgetActions from '../actions/widget.actions';

import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AnyFilterItem, isValueItem } from '../reducers/feed.reducer';
import { DialogCloseReason, DialogService } from '@sui/dialog';
import { Dimension, FilterItem, PeriodFilterItem } from '../models/core.models';
import { EMPTY, concat, from } from 'rxjs';
import {
  Gap,
  fromUnix,
  getPeriodBounds,
  toUnix,
} from 'src/app/shared/utils/date-utils';
import { WidgetType } from '../constants';
import {
  activateFilters,
  deselectFilterItem,
  selectFilterItem,
  toggleFilterDimension,
} from '../actions/feed.actions';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  getContentFilters,
  selectCurrentObject,
  selectFeedActiveFilterItems,
} from '../reducers';

import { FeedResultsPreviewDialogComponent } from '../components/popups/feed-results-preview-dialog/feed-results-preview-dialog.component';
import { FeedEntityService } from '../services/feed-entity.service';
import { Injectable, inject } from '@angular/core';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { augmentWidgetFields } from 'src/app/api/normalize/widget.normalize';
import { WidgetApiService } from 'src/app/api/queries/widget-api.service';
import { widgetDataQueriesFilter } from 'src/app/api/queries/widget.queries';
import { Router } from '@angular/router';
import { ToastService } from '@services/toast.service';
import { UtilsService } from '../services/utils.service';
import { CurrentViewWidgetsService } from '../services/current-view-widgets.service';
import { concatLatestFrom } from '@ngrx/operators';
import { of } from 'rxjs';
import { periodChanged } from '../actions/core.actions';

@Injectable()
export class WidgetEffects {
  private readonly queryClient = inject(QueryClient);
  private readonly widgetApi = inject(WidgetApiService);
  private readonly currentViewWidgets = inject(CurrentViewWidgetsService);
  private readonly feedEntity = inject(FeedEntityService);

  /**
   * Folds every widget write into the list query entry the current view observes.
   * `CurrentViewWidgetsService` resolves the key, so a patch can only ever reach
   * the *current view's* entry, never Presentation Mode's.
   *
   * `createWidgetSuccess` is absent on purpose: create **invalidates** instead,
   * because the backend appends a freshly positioned fake placeholder to a feed's
   * list and reproducing that shifting by hand is not worth it.
   */
  patchWidgetListCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          widgetActions.updateWidgetSuccess,
          widgetActions.swapWidgetTypeSuccess,
          widgetActions.massUpdateWidgetsSuccess,
          widgetActions.deleteWidgetSuccess,
          widgetActions.deleteWidgetsSuccess,
          widgetActions.widgetMassOperationSuccess,
          widgetActions.deleteWidgetsByPageId,
        ),
        tap((action) => {
          switch (action.type) {
            case widgetActions.updateWidgetSuccess.type:
              this.currentViewWidgets.patch(action.widgetId, () =>
                augmentWidgetFields(action.data),
              );
              break;

            case widgetActions.swapWidgetTypeSuccess.type:
              // ! the swap keeps the widget's slot: the response carries the new
              // ! chart but the old geometry has to be preserved, and the id
              // ! forced back so trackBy sees an update instead of remove+add
              this.currentViewWidgets.patch(action.oldWidgetId, (existing) => ({
                ...augmentWidgetFields(action.widget),
                id: action.oldWidgetId,
                x: existing.x,
                y: existing.y,
                col: existing.col,
                row: existing.row,
                cols: existing.cols,
                rows: existing.rows,
                sizeX: existing.sizeX,
                sizeY: existing.sizeY,
              }));
              break;

            case widgetActions.massUpdateWidgetsSuccess.type:
              for (const widgetId of action.widgetIds) {
                const updated = action.widgets?.find((w) => w.id === widgetId);
                if (!updated) continue;

                this.currentViewWidgets.patch(widgetId, (existing) => ({
                  ...existing,
                  ...augmentWidgetFields(updated),
                }));
              }
              break;

            case widgetActions.deleteWidgetSuccess.type:
              this.currentViewWidgets.remove([action.widgetId]);
              break;

            case widgetActions.deleteWidgetsSuccess.type:
              this.currentViewWidgets.remove(action.widgetIds);
              break;

            case widgetActions.widgetMassOperationSuccess.type:
              // * only *moved* widgets leave the current view; copies stay
              this.currentViewWidgets.remove(
                action.operations
                  .filter((operation) => operation.action === 'move')
                  .map((operation) => operation.widget_id),
              );
              break;

            case widgetActions.deleteWidgetsByPageId.type:
              this.currentViewWidgets.removePage(action.pageId);
              break;
          }
        }),
      ),
    { dispatch: false },
  );

  massUpdateWidgetsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(widgetActions.massUpdateWidgetsSuccess),
        // ! no edit-mode guard - see feed.effects filtersChanged$
        tap((action) =>
          action.widgetIds.forEach((widgetId) =>
            this.queryClient.invalidateQueries(
              widgetDataQueriesFilter(widgetId),
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  reloadWidgetAfterUpdate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          widgetActions.updateWidgetSuccess,
          widgetActions.createWidgetSuccess,
          widgetActions.swapWidgetTypeSuccess,
        ),
        // ! placeholders carry no data to reload, so skip them before invalidating
        filter((action) => {
          const widget = 'widget' in action ? action.widget : null;

          if (widget) {
            return widget.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER;
          }

          // * updateWidgetSuccess is only ever raised for a real widget
          return true;
        }),

        // ! no edit-mode guard - see feed.effects filtersChanged$

        tap((action) => {
          const widgetId =
            'widgetId' in action ? action.widgetId : action.widget.id;

          this.queryClient.invalidateQueries(widgetDataQueriesFilter(widgetId));
        }),
      ),
    { dispatch: false },
  );

  // * refetch the list when copying to the same view (same Dashboard page or same
  // * Feed) - the copies are new widgets, so the response is the only source for them
  reloadWidgetsAfterCopyToSameView$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(widgetActions.widgetMassOperationSuccess),
        withLatestFrom(this.store.select(selectCurrentObject)),
        filter(([{ operations, currentPageId }, currentObject]) => {
          return operations.some((op) => {
            if (op.action !== 'copy') return false;

            if (op.scenario === 'same-dashboard') {
              return (
                currentPageId &&
                op.target_object_ids[0] === currentObject.id &&
                op.target_page_id === currentPageId
              );
            }

            if (op.scenario === 'same-feed') {
              return op.target_object_ids[0] === currentObject.id;
            }

            // * other scenarios -> copying to different Objects, no reload needed
            return false;
          });
        }),
        tap(() => this.currentViewWidgets.invalidate()),
      ),
    { dispatch: false },
  );

  activatePivotFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.activatePivotFeed),
      switchMap((action) =>
        this.feedEntity.getFeed(action.pivotFeedId).pipe(
          map((feed) => {
            // Dispatch the toggleFilterDimension action with the loaded feed

            return toggleFilterDimension({
              dimension: feed as Dimension,
              activate: false,
              isPivot: true,
            });
          }),
          catchError(() => {
            // Show error toast and complete the effect
            this.toastService.showError('Activate Pivot Feed Failed');
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  widgetClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.htmlWidgetClicked),
      map(({ payload }) => payload),
      // parse inputs first
      map((record) => {
        const widgetId = record.widget_id as string;
        const propertyUri = record.property_uri as string;
        const rawValue = record[record.property_uri];
        const canUse =
          typeof rawValue === 'number' || typeof rawValue === 'string';

        const filterItem: AnyFilterItem | null = canUse
          ? {
              checked: true,
              kind: 'value',
              count_posts: record.y,
              property_uri: propertyUri,
              raw_value: String(rawValue),
              title: record.name,
              active: false,
              type: 'include',
            }
          : null;

        return { widgetId, propertyUri, filterItem };
      }),
      map(({ widgetId, propertyUri, filterItem }) => ({
        propertyUri,
        filterItem,
        widget: this.currentViewWidgets.byId(widgetId),
      })),
      mergeMap(({ propertyUri, filterItem, widget }) => {
        const out: Action[] = [];

        if (filterItem) {
          out.push(
            selectFilterItem({
              propertyUri,
              filterItem,
              mode: 'include',
            }),
          );
        }

        const widgetFilterDimensions = widget?.dimensions_filter ?? [];
        if (widgetFilterDimensions.length) {
          for (const filterDimension of widgetFilterDimensions) {
            out.push(
              toggleFilterDimension({
                dimension: filterDimension,
                activate: true,
                isPivot: false,
              }),
            );
          }
        }
        out.push(activateFilters());

        return out;
      }),
    ),
  );

  widgetPointClickedResolve$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.widgetPointClicked),

      map(({ widgetId, options }) => ({
        widgetId,
        options,
        widget: this.currentViewWidgets.byId(widgetId),
      })),

      switchMap(({ widgetId, options, widget }) => {
        if (widget) {
          return of(
            widgetActions.widgetPointClickedResolved({
              widgetId,
              options,
              widget,
            }),
          );
        }

        // * the clicked widget is missing from the current view's list only when
        // * the click comes from another view's widget (cross-feed point click)
        return this.widgetApi.getWidget(widgetId).pipe(
          map((fetched) =>
            widgetActions.widgetPointClickedResolved({
              widgetId,
              options,
              widget: fetched,
            }),
          ),
          catchError(() => EMPTY),
        );
      }),
    ),
  );

  widgetPointClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.widgetPointClickedResolved),

      concatLatestFrom(() => [
        this.store.select(selectFeedActiveFilterItems),
        this.store.select(getContentFilters),
        this.store.select(selectCurrentObject),
      ]),

      mergeMap(
        ([
          { widgetId, options: pointOptions, widget },
          items,
          contentFilters,
          currentObject,
        ]) => {
          const {
            datetime: isDateTime,
            name: dateValue,
            value_name: valueName,
            raw: rawValue,
            property_uri: propertyUri,
            pivot_query_id: pivotFeedId,
            zoom_start: zoomStart,
            zoom_end: zoomEnd,
            is_reset_zoom: isResetZoom,
          } = pointOptions as any;

          const isZoom = zoomStart !== undefined && zoomEnd !== undefined;

          const datasourceIds = widget?.datasources?.map((ds) => ds.id) ?? [];

          const pointDsId = (pointOptions as any).dsid;
          const targetFeedId = pointDsId || datasourceIds[0];
          const hasDifferentDatasource =
            targetFeedId && currentObject?.id !== targetFeedId;

          const actions: Action[] = [];

          const isPeriodField = UtilsService.isPeriodField(propertyUri || '');
          const shouldDispatchFilter =
            propertyUri && !isPeriodField && !pivotFeedId;

          if (!isZoom && shouldDispatchFilter) {
            const filterItem: FilterItem = {
              title: valueName,
              kind: 'value',
              raw_value: rawValue,
              count_posts: 0,
              icon: 'filter_icon',
              checked: true,
              active: false,
              property_uri: propertyUri,
              type: 'include',
            };

            actions.push(
              selectFilterItem({ propertyUri, filterItem, mode: 'include' }),
            );
          }
          if (widget?.dimensions_filter?.length) {
            for (const dimension of widget.dimensions_filter) {
              actions.push(
                toggleFilterDimension({
                  dimension,
                  activate: false,
                  isPivot: false,
                }),
              );
            }
          }

          if (isZoom) {
            const periodField = isPeriodField
              ? propertyUri
              : 'post_time_published';

            const start: Date = fromUnix(zoomStart);
            const end: Date = fromUnix(zoomEnd);

            const periodFilterItem: PeriodFilterItem = {
              kind: 'period',
              title:
                valueName ?? `${start.toISOString()} - ${end.toISOString()}`,
              time_start: zoomStart,
              time_end: zoomEnd,
              checked: true,
              active: false,
              property_uri: periodField,
              type: 'include',
            };

            if (!isResetZoom) {
              const cfStart = contentFilters?.time_start;
              const cfEnd = contentFilters?.time_end;

              if (
                typeof cfStart === 'number' &&
                typeof cfEnd === 'number' &&
                (zoomStart < cfStart || zoomEnd > cfEnd)
              ) {
                actions.push(
                  periodChanged({
                    period: {
                      startTimestamp: Math.min(cfStart, zoomStart),
                      endTimestamp: Math.max(cfEnd, zoomEnd),
                    },
                  }),
                );
              }

              actions.push(
                selectFilterItem({
                  propertyUri: periodField,
                  filterItem: periodFilterItem,
                  mode: 'include',
                }),
              );
            } else {
              actions.push(
                deselectFilterItem({
                  propertyUri: periodField,
                  filterItem: periodFilterItem,
                  mode: 'include',
                }),
              );
            }
          } else if (isDateTime) {
            const currentGap: Gap =
              (widget.settings?.['period_gap'] as Gap) || '+1DAY';
            const { startDate, endDate } = getPeriodBounds(
              dateValue,
              currentGap,
            );

            const clickedStart = toUnix(startDate);
            const clickedEnd = toUnix(endDate);

            const cfStart = contentFilters?.time_start; // unix
            const cfEnd = contentFilters?.time_end; // unix

            // If clicked period is outside current contentFilters range -> expand range
            if (
              typeof cfStart === 'number' &&
              typeof cfEnd === 'number' &&
              (clickedStart < cfStart || clickedEnd > cfEnd)
            ) {
              actions.push(
                periodChanged({
                  period: {
                    startTimestamp: Math.min(cfStart, clickedStart),
                    endTimestamp: Math.max(cfEnd, clickedEnd),
                  },
                }),
              );
            }

            const periodField = isPeriodField
              ? propertyUri
              : 'post_time_published';

            const periodFilterItem: PeriodFilterItem = {
              kind: 'period',
              title:
                valueName ??
                `${startDate.toISOString()} - ${endDate.toISOString()}`,
              time_start: toUnix(startDate),
              time_end: toUnix(endDate),
              checked: true,
              active: false,
              property_uri: periodField,
              type: 'include',
            };

            actions.push(
              selectFilterItem({
                propertyUri,
                filterItem: periodFilterItem,
                mode: 'include',
              }),
            );
          }

          if (pivotFeedId) {
            actions.push(
              widgetActions.activatePivotFeed({ widgetId, pivotFeedId }),
            );
          }

          const isNewItem =
            shouldDispatchFilter &&
            !items.some(
              (item) =>
                isValueItem(item) &&
                item.property_uri === propertyUri &&
                item.raw_value === rawValue,
            );

          const shouldActivateFilters =
            isNewItem || items.length === 0 || isDateTime || !!pivotFeedId;

          if (hasDifferentDatasource) {
            const pointFilters = {
              ...(contentFilters || {}),
            };

            const isPeriodField = UtilsService.isPeriodField(propertyUri || '');

            // Value filter from point click
            if (
              propertyUri &&
              rawValue !== undefined &&
              !isPeriodField &&
              !pivotFeedId
            ) {
              pointFilters[propertyUri] = { include: [rawValue], exclude: [] };
            }

            // Period filter from datetime point click
            if (isDateTime) {
              const currentGap: Gap =
                (widget.settings?.['period_gap'] as Gap) || '+1DAY';
              const { startDate, endDate } = getPeriodBounds(
                dateValue,
                currentGap,
              );
              const periodField = isPeriodField
                ? propertyUri
                : 'post_time_published';
              pointFilters[periodField] = {
                include: [{ start: toUnix(startDate), end: toUnix(endDate) }],
                exclude: [],
              };
            }

            const dialogRef = this.dialogService.openSimpleDialog(
              FeedResultsPreviewDialogComponent,
              {
                data: {
                  feedId: targetFeedId,
                  contentFilters: {
                    time_start: pointFilters?.time_start,
                    time_end: pointFilters?.time_end,
                    sort: pointFilters?.sort,
                    order: pointFilters?.order,
                    ...(Object.keys(pointFilters).length > 0
                      ? { filters: pointFilters }
                      : {}),
                  },

                  pivotFeedId: pivotFeedId,
                  filterDimensions: widget?.dimensions_filter ?? [],
                  filterLabel: valueName,
                },
                size: 'large',
              },
            );

            return dialogRef.closed.pipe(
              take(1),
              switchMap((event) => {
                if (
                  event.reason !== DialogCloseReason.Confirm ||
                  !(event.data as any)?.navigate
                ) {
                  return EMPTY;
                }

                const workspaceId = (event.data as any).workspaceId;

                // Navigate first, then dispatch filter actions after navigation succeeds

                return from(
                  this.router.navigate([
                    'analytics',
                    'workspace',
                    workspaceId,
                    'feed',
                    targetFeedId,
                  ]),
                ).pipe(
                  filter((success) => success),
                  switchMap(() => {
                    if (pivotFeedId && shouldActivateFilters) {
                      return concat(
                        from(actions),
                        this.actions$.pipe(
                          ofType(toggleFilterDimension),
                          filter((action) => action.isPivot === true),
                          take(1),
                          map(() => activateFilters()),
                        ),
                      );
                    }

                    if (shouldActivateFilters) {
                      actions.push(activateFilters());
                    }

                    return from(actions);
                  }),
                );
              }),
            );
          }

          // "wait" when pivotFeedId is present
          if (pivotFeedId && shouldActivateFilters) {
            return concat(
              // dispatch everything you already queued (includes activatePivotFeed)
              from(actions),

              // then wait for pivot load success/resolved toggleFilterDimension (only the pivot trigger)
              this.actions$.pipe(
                ofType(toggleFilterDimension),
                filter((action) => action.isPivot === true),
                take(1),
                map(() => activateFilters()),
              ),
            );
          }

          // normal path (no pivot waiting)
          if (shouldActivateFilters) {
            actions.push(activateFilters());
          }

          return actions;
        },
      ),
    ),
  );
  constructor(
    private actions$: Actions,
    private store: Store,
    private toastService: ToastService,
    private dialogService: DialogService,
    private router: Router,
  ) {}
}
